import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { useAuth0 } from "../../../auth0/react-auth0-wrapper";
import UserRoleContext from "../../../context/userrolecontext";
import LanguageContext from "../../../context/languagecontext";
import { Link } from "react-router-dom";
import Styles from "./Header.module.scss";

const apiUri = process.env.REACT_APP_API_URI;

const Header = props => {
  const userRole = React.useContext(UserRoleContext);
  const { language, setLanguage } = React.useContext(LanguageContext);

  const { isAuthenticated, logout, user, getTokenSilently } = useAuth0();
  const node = React.useRef();
  const [open, setOpen] = useState(false);

  const handleLanguageSwitch = async e => {
    const language = e.target.getAttribute("data-name");

    try {
      const token = await getTokenSilently();

      const response = await fetch(`${apiUri}/auth0/switchlanguage`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json",
          "Content-Type": "application/json"
        },
        body: JSON.stringify({ language: language })
      });
      if (!response.ok) {
        throw Error(`${response.status}`);
      }
      const answer = await response.json();
      if (answer && answer.success) {
        setLanguage(language);
      }
    } catch (err) {
      return;
    }
  };

  const handleClickOutside = e => {
    if (node.current.contains(e.target)) {
      // inside click
      return;
    }
    setOpen(false);
    // outside click
  };

  const enterPressed = e => {
    let code = e.keyCode || e.which;
    if (code === 13) {
      setOpen(!open);
    }
  };

  const enterPressedLogout = e => {
    let code = e.keyCode || e.which;
    if (code === 13) {
      logout();
    }
  };

  useEffect(() => {
    if (open) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [open]);

  return (
    <>
      <Helmet>
        <html lang={language} />
      </Helmet>
      <header className={Styles.header}>
        <Link to="/" style={{ display: "block", lineHeight: 0 }}>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 113.38 27.86"
            className={Styles.logo}
          >
            <path
              fill="#263d42"
              d="M45.44 24.03c-5.57 0-10.1-4.53-10.1-10.1 0-5.57 4.53-10.1 10.1-10.1 5.57 0 10.1 4.53 10.1 10.1 0 5.57-4.53 10.1-10.1 10.1zm0-18.7c-4.74 0-8.6 3.86-8.6 8.6s3.86 8.6 8.6 8.6 8.6-3.86 8.6-8.6-3.85-8.6-8.6-8.6zM32.07 14.68H.75c-.41 0-.75-.34-.75-.75s.34-.75.75-.75h31.32c.41 0 .75.34.75.75s-.34.75-.75.75z"
            />
            <path
              fill="#263d42"
              d="M88.95 3.43c.26.28.65.42 1.17.42.51 0 .9-.14 1.16-.42.26-.28.39-.63.39-1.05 0-.43-.13-.79-.39-1.08-.26-.3-.65-.45-1.16-.45-.52 0-.91.15-1.17.44-.26.29-.39.66-.39 1.08 0 .43.13.78.39 1.06M83.75 8.61h5.15v10.65h-5.15v2.24h12.68v-2.24h-4.94V6.36h-7.74zM68.3 6.36h-2.66l6.23 15.14h1.98l6.17-15.14h-2.65l-4.24 11.42-.25.94-.24-.94zM112.99 10.69c-.26-.95-.64-1.76-1.15-2.44-.5-.68-1.13-1.21-1.88-1.6-.75-.38-1.61-.57-2.58-.57-.93 0-1.75.16-2.45.49-.7.33-1.3.8-1.79 1.41V0h-2.59v21.5h2.38l.13-1.72c.5.64 1.11 1.14 1.83 1.48.71.35 1.56.52 2.53.52.96 0 1.81-.2 2.56-.59.74-.39 1.36-.93 1.87-1.62.5-.69.89-1.51 1.15-2.44.26-.94.39-1.95.39-3.03v-.3c-.01-1.12-.14-2.16-.4-3.11zm-2.2 3.41c0 .74-.08 1.44-.23 2.1-.15.66-.39 1.25-.71 1.75-.32.5-.74.9-1.25 1.2-.51.29-1.12.44-1.83.44-.45 0-.86-.06-1.23-.18a3.53 3.53 0 0 1-1.01-.5c-.3-.21-.56-.46-.79-.75-.23-.28-.43-.6-.59-.93v-6.62c.16-.34.35-.65.58-.93.23-.28.49-.53.78-.74.29-.21.63-.37 1-.49s.78-.18 1.23-.18c.75 0 1.38.15 1.89.45.51.3.93.7 1.25 1.21.32.51.55 1.1.69 1.76.14.67.22 1.37.22 2.11v.3z"
            />
          </svg>
        </Link>

        <div className={Styles.navigation}>
          {props.showBackButton && (
            <Link
              to={
                props.superUserToOrganisations &&
                userRole.role === "vib-superuser"
                  ? "/organisations/"
                  : props.backButtonLink
              }
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 512 512"
                className={Styles.arrowBackIcon}
              >
                <path d="M256 0C114.6 0 0 114.6 0 256s114.6 256 256 256 256-114.6 256-256S397.4 0 256 0zm123.6 289.7c0 5-4 9-9 9H257.9c-5 0-9 4-9 9v55c0 8-9.7 12-15.4 6.4L126.7 262.4c-3.5-3.5-3.5-9.2 0-12.7l106.8-106.8c5.7-5.7 15.4-1.7 15.4 6.4v55c0 5 4 9 9 9h112.8c5 0 9 4 9 9v67.4z" />
              </svg>
            </Link>
          )}

          {isAuthenticated && (
            <div className={Styles.dropdown} ref={node}>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 512 512"
                className={Styles.userIcon}
                tabIndex="0"
                onClick={() => setOpen(!open)}
                onKeyPress={e => enterPressed(e)}
              >
                <path d="M347.1 282.1h-5.3c-25.3 14.5-54.5 22.7-85.8 22.7s-60.5-8.3-85.8-22.7h-5.3C73.8 282.1 0 350.3 0 434.3v31.1C0 491.2 22.6 512 50.4 512h411.1c27.9 0 50.4-20.8 50.4-46.6v-31.1c.1-84-73.7-152.2-164.8-152.2zM256 0c-73 0-132.1 59.1-132.1 132.1S183 264.2 256 264.2s132.1-59.1 132.1-132.1S329 0 256 0z" />
              </svg>
              {open && (
                <div className={Styles.dropdownContent}>
                  <span className={Styles.user} title={user.name}>
                    {language === "fr"
                      ? "Connecté sous "
                      : language === "it"
                      ? "Loggato come"
                      : "Angemeldet als"}
                    :
                    <br />
                    {user.name}
                  </span>

                  <span className={Styles.languageSwitcher}>
                    <span className={Styles.languages}>
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 512 512"
                      >
                        <path d="M341.17 194.17H170.83c-1.86 20-2.83 40.71-2.83 61.83 0 21.12.97 41.83 2.83 61.83h170.34c1.86-20 2.83-40.71 2.83-61.83 0-21.12-.97-41.83-2.83-61.83zM312.71 445.37c9.14-22.72 16.41-48.96 21.64-77.54h-156.7c5.23 28.58 12.5 54.81 21.64 77.54C215.82 486.47 237.55 512 256 512c18.45 0 40.18-25.53 56.71-66.63zM359.1 47.98c11.31 28.12 20.06 60.76 25.97 96.19h101.26c-28.9-59.41-80.1-105.96-142.71-128.77 5.53 9.82 10.7 20.7 15.48 32.58zM199.29 66.63c-9.14 22.72-16.41 48.96-21.64 77.54h156.7c-5.23-28.58-12.5-54.81-21.64-77.54C296.18 25.53 274.45 0 256 0c-18.45 0-40.18 25.53-56.71 66.63zM391.35 194.17C393.1 214.29 394 234.99 394 256c0 21.01-.9 41.71-2.65 61.83h113.11A256.61 256.61 0 00512 256c0-21.32-2.62-42.03-7.54-61.83H391.35zM385.07 367.83c-5.91 35.43-14.67 68.07-25.97 96.19-4.78 11.88-9.95 22.75-15.47 32.58 62.62-22.81 113.81-69.37 142.71-128.77H385.07zM126.93 144.17c5.91-35.43 14.67-68.07 25.97-96.19 4.78-11.88 9.95-22.75 15.47-32.58-62.61 22.8-113.81 69.36-142.7 128.77h101.26zM152.9 464.02c-11.31-28.12-20.06-60.76-25.97-96.19H25.66c28.9 59.41 80.1 105.96 142.71 128.77-5.52-9.82-10.69-20.7-15.47-32.58zM120.65 317.83C118.9 297.71 118 277.01 118 256c0-21.01.9-41.71 2.65-61.83H7.54C2.62 213.97 0 234.68 0 256s2.62 42.03 7.54 61.83h113.11z" />
                      </svg>
                      <span
                        onClick={e => handleLanguageSwitch(e)}
                        data-name="de"
                        className={`${Styles.language} ${
                          language === "de" ? Styles.active : null
                        }`}
                        tabIndex="0"
                      >
                        DE
                      </span>
                      &nbsp;&nbsp;|&nbsp;&nbsp;
                      <span
                        onClick={e => handleLanguageSwitch(e)}
                        data-name="fr"
                        className={`${Styles.language} ${
                          language === "fr" ? Styles.active : null
                        }`}
                        tabIndex="0"
                      >
                        FR
                      </span>
                      &nbsp;&nbsp;|&nbsp;&nbsp;
                      <span
                        onClick={e => handleLanguageSwitch(e)}
                        data-name="it"
                        className={`${Styles.language} ${
                          language === "it" ? Styles.active : null
                        }`}
                        tabIndex="0"
                      >
                        IT
                      </span>
                    </span>
                  </span>

                  {userRole.role !== "user" && (
                    <span>
                      <Link to="/" tabIndex="0">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 512 512"
                        >
                          <rect
                            x="177.23"
                            y="74.7"
                            width="279.05"
                            height="66.48"
                          />
                          <rect
                            x="177.23"
                            y="222.76"
                            width="279.05"
                            height="66.48"
                          />
                          <rect
                            x="177.23"
                            y="370.82"
                            width="279.05"
                            height="66.48"
                          />
                          <rect
                            x="55.72"
                            y="74.7"
                            width="84.03"
                            height="66.48"
                          />
                          <rect
                            x="55.72"
                            y="222.76"
                            width="84.03"
                            height="66.48"
                          />
                          <rect
                            x="55.72"
                            y="370.82"
                            width="84.03"
                            height="66.48"
                          />
                        </svg>
                        {language === "fr"
                          ? "Aperçu des machines"
                          : language === "it"
                          ? "Lista macchine"
                          : "Maschinen-Übersicht"}
                      </Link>
                    </span>
                  )}

                  {userRole.role === "vib-superuser" ? (
                    <span>
                      <Link to="/organisations" tabIndex="0">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 512 512"
                        >
                          <path d="M489.8 310.2l-39.5-22.8c1.6-10.2 2.5-20.7 2.5-31.4s-.9-21.2-2.5-31.4l39.5-22.8c8.8-5.1 12.8-15.6 9.6-25.2-11.2-34.4-29.5-65.6-53.2-91.9-6.7-7.5-17.8-9.1-26.5-4.1l-39.4 22.8C364.1 90.2 345.8 79.5 326 72V26.5c0-10.1-7-18.8-16.8-20.9C292 1.9 274.2 0 256 0s-36 1.9-53.2 5.5C193 7.6 186 16.4 186 26.5V72c-19.8 7.6-38.1 18.2-54.3 31.4L92.3 80.6c-8.7-5-19.8-3.4-26.5 4.1-23.7 26.3-42 57.5-53.2 91.9-3.1 9.6.9 20.2 9.6 25.2l39.5 22.8c-1.6 10.2-2.5 20.7-2.5 31.4s.9 21.2 2.5 31.4l-39.5 22.8c-8.8 5.1-12.8 15.6-9.6 25.2 11.2 34.4 29.5 65.6 53.2 91.9 6.7 7.5 17.8 9.1 26.5 4.1l39.4-22.8c16.2 13.2 34.5 23.9 54.3 31.4v45.5c0 10.1 7 18.8 16.8 20.9 17.2 3.6 34.9 5.5 53.2 5.5s36-1.9 53.2-5.5c9.8-2.1 16.8-10.9 16.8-20.9V440c19.8-7.6 38.1-18.2 54.3-31.4l39.4 22.8c8.7 5 19.8 3.4 26.5-4.1 23.7-26.3 42-57.5 53.2-91.9 3.2-9.7-.8-20.2-9.6-25.2zM256 350.7c-52.3 0-94.7-42.4-94.7-94.7s42.4-94.7 94.7-94.7 94.7 42.4 94.7 94.7-42.4 94.7-94.7 94.7z" />
                        </svg>
                        {language === "fr"
                          ? "Organisations"
                          : language === "it"
                          ? "Organizzazioni"
                          : "Organisationen"}
                      </Link>
                    </span>
                  ) : userRole.role === "admin" ? (
                    <span>
                      <Link
                        to={`/organisations/${userRole.organisation}/machinegroups`}
                        tabIndex="0"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 512 512"
                        >
                          <path d="M489.8 310.2l-39.5-22.8c1.6-10.2 2.5-20.7 2.5-31.4s-.9-21.2-2.5-31.4l39.5-22.8c8.8-5.1 12.8-15.6 9.6-25.2-11.2-34.4-29.5-65.6-53.2-91.9-6.7-7.5-17.8-9.1-26.5-4.1l-39.4 22.8C364.1 90.2 345.8 79.5 326 72V26.5c0-10.1-7-18.8-16.8-20.9C292 1.9 274.2 0 256 0s-36 1.9-53.2 5.5C193 7.6 186 16.4 186 26.5V72c-19.8 7.6-38.1 18.2-54.3 31.4L92.3 80.6c-8.7-5-19.8-3.4-26.5 4.1-23.7 26.3-42 57.5-53.2 91.9-3.1 9.6.9 20.2 9.6 25.2l39.5 22.8c-1.6 10.2-2.5 20.7-2.5 31.4s.9 21.2 2.5 31.4l-39.5 22.8c-8.8 5.1-12.8 15.6-9.6 25.2 11.2 34.4 29.5 65.6 53.2 91.9 6.7 7.5 17.8 9.1 26.5 4.1l39.4-22.8c16.2 13.2 34.5 23.9 54.3 31.4v45.5c0 10.1 7 18.8 16.8 20.9 17.2 3.6 34.9 5.5 53.2 5.5s36-1.9 53.2-5.5c9.8-2.1 16.8-10.9 16.8-20.9V440c19.8-7.6 38.1-18.2 54.3-31.4l39.4 22.8c8.7 5 19.8 3.4 26.5-4.1 23.7-26.3 42-57.5 53.2-91.9 3.2-9.7-.8-20.2-9.6-25.2zM256 350.7c-52.3 0-94.7-42.4-94.7-94.7s42.4-94.7 94.7-94.7 94.7 42.4 94.7 94.7-42.4 94.7-94.7 94.7z" />
                        </svg>
                        {language === "fr"
                          ? "Groupes de machines"
                          : language === "it"
                          ? "Gruppi di macchine"
                          : "Maschinengruppen"}
                      </Link>
                    </span>
                  ) : null}

                  <span>
                    <Link to="/passwordchange" tabIndex="0">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 512 512"
                      >
                        <path d="M442.7 245.33H168.02v-76.7c0-48.51 39.47-87.98 87.98-87.98s87.97 39.47 87.97 87.98c0 22.09 17.91 40 40 40s40-17.91 40-40C423.97 76 348.62.65 256 .65S88.02 76 88.02 168.62v76.7H69.3c-18.51 0-33.52 15.01-33.52 33.52v199.54c0 18.51 15.01 33.52 33.52 33.52h373.4c18.51 0 33.52-15.01 33.52-33.52V278.84c-.01-18.51-15.01-33.51-33.52-33.51zM296 413.17c0 22.09-17.91 40-40 40s-40-17.91-40-40v-69.11c0-22.09 17.91-40 40-40s40 17.91 40 40v69.11z" />
                      </svg>
                      {language === "fr"
                        ? "Changer de mot de passe"
                        : language === "it"
                        ? "Cambiare la password"
                        : "Passwort ändern"}
                    </Link>
                  </span>

                  <span
                    onClick={() => logout()}
                    tabIndex="0"
                    onKeyPress={e => enterPressedLogout(e)}
                    className={Styles.cursor}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 512 512"
                    >
                      <path d="M256 279.3c-23.5 0-42.5-19-42.5-42.5V42.5C213.5 19 232.5 0 256 0s42.5 19 42.5 42.5v194.3c0 23.5-19 42.5-42.5 42.5z" />
                      <path d="M256 512c-68.4 0-132.7-26.6-181-75C26.6 388.7 0 324.4 0 256c0-34.9 6.9-68.7 20.5-100.5 13.1-30.7 31.9-58.2 55.8-81.8 16.7-16.5 43.7-16.3 60.2.4s16.3 43.7-.4 60.1c-32.9 32.4-51 75.6-51 121.7 0 94.3 76.7 170.9 170.9 170.9S426.9 350.3 426.9 256c0-46.1-18.1-89.4-51-121.7-16.7-16.5-16.9-43.4-.4-60.1 16.5-16.7 43.4-16.9 60.1-.4 23.9 23.5 42.7 51.1 55.8 81.8C505.1 187.3 512 221.1 512 256c0 68.4-26.6 132.7-75 181s-112.6 75-181 75z" />
                    </svg>
                    {language === "fr"
                      ? "Déconnexion"
                      : language === "it"
                      ? "Disconnettere"
                      : "Abmelden"}
                  </span>
                </div>
              )}
            </div>
          )}
        </div>
      </header>
    </>
  );
};

export default Header;
